"use client";

import useLockScroll from "@/hooks/useLockScroll";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

export type MediaItem = {
  type: string;
  src: string;
  thumb?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  media: MediaItem[];
};

export default function GalleryModal({ open, onClose, media }: Props) {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [mounted, setMounted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragLimit = 500 * zoom;
  const current = media[index];

  const next = () => setIndex((i) => (i + 1) % media.length);
  const prev = () => setIndex((i) => (i - 1 + media.length) % media.length);
  // full screen toggle

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  /* ---------------- zoom controls ---------------- */

  const zoomIn = () => setZoom((z) => Math.min(z + 0.3, 4));

  const zoomOut = () =>
    setZoom((z) => {
      const next = Math.max(z - 0.3, 1);
      return next;
    });

  /* scroll wheel zoom */

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();

    setZoom((z) => {
      let next = z;

      if (e.deltaY > 0) next = Math.max(z - 0.2, 1);
      else next = Math.min(z + 0.2, 4);

      return next;
    });
  };

  /* ---------------- image change ---------------- */

  const changeImage = (i: number) => {
    setIndex(i);
    setZoom(1);
  };

  /* ---------------- lock page scroll ---------------- */
  useLockScroll(open);

  /* ---------------- mount fix for next SSR ---------------- */

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const nextItem = media[index + 1];
    if (!nextItem || nextItem.type !== "image") return;

    const img = new window.Image();
    img.src = nextItem.src;
  }, [index]);
  /* ---------------- keyboard navigation ---------------- */

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();

      if (e.key === "ArrowRight") next();

      if (e.key === "ArrowLeft") prev();

      if (e.key === "+") zoomIn();

      if (e.key === "-") zoomOut();
    };

    window.addEventListener("keydown", handler);

    return () => window.removeEventListener("keydown", handler);
  }, []);

  if (!open || !mounted) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-modal bg-black/75 flex flex-col backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute right-6 top-6 flex items-center gap-3 z-20">
          <button
            onClick={zoomIn}
            className="w-10 h-10 bg-black/60 text-white rounded flex items-center justify-center hover:bg-black"
          >
            +
          </button>

          <button
            onClick={zoomOut}
            className="w-10 h-10 bg-black/60 text-white rounded flex items-center justify-center hover:bg-black"
          >
            −
          </button>

          <button
            onClick={toggleFullscreen}
            className="w-10 h-10 bg-black/60 text-white rounded flex items-center justify-center hover:bg-black"
          >
            ⛶
          </button>

          <button
            onClick={onClose}
            className="w-10 h-10 bg-black/60 text-white rounded flex items-center justify-center hover:bg-black"
          >
            ✕
          </button>
        </div>

        {/* main preview */}

        <div
          className="flex-1 flex items-center justify-center overflow-hidden"
          onWheel={handleWheel}
        >
          {current.type === "image" && (
            <motion.div
              key={`${index}-${zoom === 1 ? "reset" : "zoom"}`}
              drag={zoom > 1}
              dragElastic={0.1}
              dragMomentum={false}
              dragConstraints={{
                left: -dragLimit,
                right: dragLimit,
                top: -dragLimit,
                bottom: dragLimit,
              }}
              whileTap={{ cursor: "grabbing" }}
              className="cursor-grab touch-none"
              style={{ scale: zoom }}
              onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2))}
            >
              <Image
                src={current.src}
                alt=""
                width={4000}
                height={3000}
                sizes="(max-width: 768px) 100vw, 1600px"
                quality={100}
                priority
                draggable={false}
                className="object-contain max-h-[85vh] w-auto select-none pointer-events-none"
              />
            </motion.div>
          )}

          {current.type === "video" && (
            <div className="relative z-10 w-full max-w-4xl aspect-video ">
              <iframe
                src={current.src}
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full z-0"
              />
            </div>
          )}
        </div>

        {/* thumbnails */}

        <div className="h-[140px] flex items-center gap-4 overflow-x-auto px-10 pb-2">
          {media.map((m, i) => {
            const active = i === index;
            const type = m.type?.toLowerCase();

            if (type !== "image" && type !== "video") return null;

            return (
              <div
                key={i}
                onClick={() => changeImage(i)}
                className={`
                relative w-[160px] h-[100px] shrink-0 cursor-pointer
                border-2 transition
                ${active ? "border-white scale-105" : "border-transparent"}
                `}
              >
                {type === "image" && (
                  <Image
                    src={m.thumb || m.src}
                    alt=""
                    fill
                    sizes="160px"
                    quality={50}
                    className="object-cover"
                  />
                )}

                {type === "video" && (
                  <>
                    <Image
                      src={m.thumb || ""}
                      alt=""
                      fill
                      sizes="160px"
                      quality={50}
                      className="object-cover"
                    />

                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-black/70 flex items-center justify-center text-white text-lg">
                        ▶
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}
